import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Divider, NumberFormatter } from '@mantine/core';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp
} from 'tabler-icons-react';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import * as classes from '../../../styles/nestedStyles.module.css';
import AppImage from '../../common/AppImage';
import CustomTooltip from '../../common/CustomTooltip';
import { singularPluralFormat } from '../../../helpers/format';

const AdminVendorPayoutProductListItem = ({
  ecomOrderProduct,
  disableExpand
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const productValueTotals =
    ecomOrderProduct.ecomOrderProducts.reduce(
      (r, c) => ({
        totalProductCost: r.totalProductCost + c.productCost * c.quantity,
        totalEligibleAmount:
          r.totalEligibleAmount + c.productCost * c.qualifiedQuantity,
        quantity: r.quantity + c.quantity,
        qualifiedQuantity: r.qualifiedQuantity + c.qualifiedQuantity,
        returnCount: r.returnCount + (c.returnCount || 0),
        variantUuids: [...new Set([...r.variantUuids, c.variantUuid])]
      }),
      {
        totalProductCost: 0,
        totalEligibleAmount: 0,
        quantity: 0,
        qualifiedQuantity: 0,
        returnCount: 0,
        variantUuids: []
      }
    ) || {};

  return ecomOrderProduct ? (
    <AppStack style={{ gap: 0 }}>
      <AppFlexbox
        className={!disableExpand ? classes['hover-action-card'] : null}
        onClick={() => !disableExpand && setIsExpanded(!isExpanded)}
        style={{
          padding: 16,
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 72,
          cursor: 'pointer'
        }}
      >
        <AppFlexbox style={{ alignItems: 'start', gap: 8 }}>
          <AppStack
            style={{
              width: 50,
              height: 50,
              borderRadius: 8,
              overflow: 'hidden',
              border: !ecomOrderProduct.previewImage
                ? '1px solid lightgrey'
                : 'none'
            }}
          >
            <AppImage
              alt={ecomOrderProduct.productName}
              src={ecomOrderProduct.previewImage}
            />
          </AppStack>

          <AppStack style={{ gap: 5 }}>
            <AppStack style={{ gap: 0 }}>
              <AppFlexbox style={{ gap: 8 }}>
                <AppText style={{ fontSize: 14 }}>
                  <span style={{ fontWeight: 500 }}>
                    {ecomOrderProduct.productName}
                  </span>{' '}
                  <span style={{ color: '#666' }}>
                    x{productValueTotals.quantity}
                  </span>
                </AppText>
              </AppFlexbox>
            </AppStack>

            {(productValueTotals.returnCount > 0 ||
              productValueTotals.qualifiedQuantity !==
                productValueTotals.quantity) && (
              <>
                <Divider />
                <AppStack style={{ gap: 0 }}>
                  {productValueTotals.qualifiedQuantity !==
                    productValueTotals.quantity && (
                    <AppFlexbox>
                      <CustomTooltip
                        label={
                          <AppStack style={{ gap: 8 }}>
                            <AppText style={{ fontSize: 14 }}>
                              {singularPluralFormat(
                                productValueTotals.quantity -
                                  productValueTotals.qualifiedQuantity,
                                'product',
                                'products'
                              )}{' '}
                              do not qualify for the payout because they are
                              part of a group order that is still accumulating.
                            </AppText>
                          </AppStack>
                        }
                        width={300}
                      >
                        <AppFlexbox style={{ alignItems: 'center', gap: 5 }}>
                          <AppText style={{ fontSize: 14, color: '#C40000' }}>
                            {productValueTotals.qualifiedQuantity}/
                            {productValueTotals.quantity} qualify
                          </AppText>
                          <AlertCircle color="#C40000" size={16} />
                        </AppFlexbox>
                      </CustomTooltip>
                    </AppFlexbox>
                  )}
                  {productValueTotals.returnCount > 0 && (
                    <AppText style={{ fontSize: 14, color: '#C40000' }}>
                      {productValueTotals.returnCount} cancelled
                    </AppText>
                  )}
                </AppStack>
              </>
            )}
          </AppStack>
        </AppFlexbox>

        <AppFlexbox
          style={{
            alignItems: 'center',
            justifyContent: 'end',
            textAlign: 'end',
            gap: 8
          }}
        >
          {productValueTotals.qualifiedQuantity !==
            productValueTotals.quantity && (
            <>
              <NumberFormatter
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                style={{
                  fontSize: 14,
                  textDecoration: 'line-through'
                }}
                thousandSeparator
                value={productValueTotals.totalProductCost / 100}
              />

              <ChevronRight size={18} />
            </>
          )}
          <NumberFormatter
            decimalScale={2}
            fixedDecimalScale
            prefix="$"
            style={{ fontSize: 14 }}
            thousandSeparator
            value={productValueTotals.totalEligibleAmount / 100}
          />

          {isExpanded ? (
            <ChevronUp color="#999" size={18} />
          ) : (
            <ChevronDown color="#999" size={18} />
          )}
        </AppFlexbox>
      </AppFlexbox>

      {!disableExpand && isExpanded && (
        <>
          <Divider />
          <AppStack style={{ gap: 0, backgroundColor: '#F1F1F1' }}>
            {productValueTotals.variantUuids.map((variantUuid, index) => {
              const variantProducts = ecomOrderProduct.ecomOrderProducts.filter(
                (p) => p.variantUuid === variantUuid
              );

              const variantTotals =
                variantProducts.reduce(
                  (r, c) => ({
                    totalProductCost:
                      r.totalProductCost + c.productCost * c.quantity,
                    totalEligibleAmount:
                      r.totalEligibleAmount +
                      c.productCost * c.qualifiedQuantity,
                    quantity: r.quantity + c.quantity,
                    qualifiedQuantity:
                      r.qualifiedQuantity + c.qualifiedQuantity,
                    returnCount: r.returnCount + (c.returnCount || 0)
                  }),
                  {
                    totalProductCost: 0,
                    totalEligibleAmount: 0,
                    quantity: 0,
                    qualifiedQuantity: 0,
                    returnCount: 0
                  }
                ) || {};

              return (
                <React.Fragment key={variantUuid}>
                  {index > 0 && <Divider />}
                  <AppFlexbox
                    key={variantUuid}
                    style={{
                      padding: 16,
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <AppStack style={{ gap: 5 }}>
                      <AppText style={{ fontSize: 14 }}>
                        {variantProducts[0].variantOptions.length === 0
                          ? 'Default'
                          : variantProducts[0].variantOptions
                              .sort((a, b) => a.nameSort - b.nameSort)
                              .map((op) => op.value)
                              .join(' / ')}{' '}
                        <span style={{ color: '#666' }}>
                          x{variantTotals.quantity}
                        </span>
                      </AppText>
                      {(variantTotals.returnCount > 0 ||
                        variantTotals.qualifiedQuantity !==
                          variantTotals.quantity) && (
                        <AppStack style={{ gap: 0 }}>
                          {variantTotals.qualifiedQuantity !==
                            variantTotals.quantity && (
                            <AppFlexbox
                              style={{ alignItems: 'center', gap: 5 }}
                            >
                              <AppText
                                style={{ fontSize: 14, color: '#C40000' }}
                              >
                                {variantTotals.qualifiedQuantity}/
                                {variantTotals.quantity} qualify
                              </AppText>
                              <AlertCircle color="#C40000" size={16} />
                            </AppFlexbox>
                          )}
                          {variantTotals.returnCount > 0 && (
                            <AppText style={{ fontSize: 14, color: '#C40000' }}>
                              {variantTotals.returnCount} cancelled
                            </AppText>
                          )}
                        </AppStack>
                      )}
                    </AppStack>

                    <AppFlexbox
                      style={{
                        alignItems: 'center',
                        justifyContent: 'end',
                        textAlign: 'end',
                        gap: 8
                      }}
                    >
                      {variantTotals.qualifiedQuantity !==
                        variantTotals.quantity && (
                        <>
                          <NumberFormatter
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="$"
                            style={{
                              fontSize: 14,
                              textDecoration: 'line-through'
                            }}
                            thousandSeparator
                            value={variantTotals.totalProductCost / 100}
                          />

                          <ChevronRight size={18} />
                        </>
                      )}
                      <NumberFormatter
                        decimalScale={2}
                        fixedDecimalScale
                        prefix="$"
                        style={{ fontSize: 14 }}
                        thousandSeparator
                        value={variantTotals.totalEligibleAmount / 100}
                      />
                    </AppFlexbox>
                  </AppFlexbox>
                </React.Fragment>
              );
            })}
          </AppStack>
        </>
      )}
    </AppStack>
  ) : null;
};

AdminVendorPayoutProductListItem.propTypes = {
  disableExpand: PropTypes.bool,
  ecomOrderProduct: PropTypes.object
};

export default AdminVendorPayoutProductListItem;
